import { WorkOrderConditionFlowStep } from "@redotech/redo-flows-common/advanced-flow-db-parser";
import { getConditionTypeLabel } from "@redotech/redo-flows-common/oms/conditions";
import { OmsConditionType } from "@redotech/redo-flows-core-common/oms-condition-type";
import { t, useTranslation } from "@redotech/redo-i18n";
import BranchIcon from "@redotech/redo-icons/git-branch-01.svg";
import { RedoFeaturedIcon } from "@redotech/redo-web/arbiter-components/featured-icon/redo-featured-icon";
import { Flex } from "@redotech/redo-web/flex";
import { Text } from "@redotech/redo-web/text";
import { Handle, Position } from "@xyflow/react";
import { memo } from "react";
import { DeepPartial, PartialWithRequiredType } from "../../steps";
import { BaseNode } from "../base-node";
import { FieldToListMatchDetails } from "../common/field-to-list-match-details";
import * as NodeCSS from "../node.module.css";
import { IntegerComparisonDetails } from "../oms/integer-comparison-details";

export interface WorkOrderConditionNodeData {
  step: PartialWithRequiredType<WorkOrderConditionFlowStep>;
  deleteElements: (id: string) => void;
}

function getDetails(step: DeepPartial<WorkOrderConditionFlowStep>) {
  switch (step.conditionType) {
    case OmsConditionType.SKU:
      return (
        <FieldToListMatchDetails
          field={getConditionTypeLabel(OmsConditionType.SKU)}
          fieldLabelIsPlural
          matchType={step.matchType}
          matchValues={step.matchValues}
        />
      );
    case OmsConditionType.PRODUCT_NAME:
      return (
        <FieldToListMatchDetails
          field={getConditionTypeLabel(OmsConditionType.PRODUCT_NAME)}
          fieldLabelIsPlural
          matchType={step.matchType}
          matchValues={step.matchValues}
        />
      );
    case OmsConditionType.VARIANT_TITLE:
      return (
        <FieldToListMatchDetails
          field={getConditionTypeLabel(OmsConditionType.VARIANT_TITLE)}
          fieldLabelIsPlural
          matchType={step.matchType}
          matchValues={step.matchValues}
        />
      );
    case OmsConditionType.PRODUCT_TYPE:
      return (
        <FieldToListMatchDetails
          field={getConditionTypeLabel(OmsConditionType.PRODUCT_TYPE)}
          fieldLabelIsPlural
          matchType={step.matchType}
          matchValues={step.matchValues}
        />
      );
    case OmsConditionType.PRODUCT_TAG:
      return (
        <FieldToListMatchDetails
          field={getConditionTypeLabel(OmsConditionType.PRODUCT_TAG)}
          fieldLabelIsPlural
          matchType={step.matchType}
          matchValues={step.matchValues}
        />
      );
    case OmsConditionType.ITEM_QUANTITY:
      return (
        <IntegerComparisonDetails
          field={getConditionTypeLabel(OmsConditionType.ITEM_QUANTITY)}
          matchType={step.matchType}
          matchValue={step.matchValue as number | undefined}
        />
      );
    case OmsConditionType.VENDOR:
      return (
        <FieldToListMatchDetails
          field={getConditionTypeLabel(OmsConditionType.VENDOR)}
          fieldLabelIsPlural={false}
          matchType={step.matchType}
          matchValues={step.matchValues}
        />
      );
    case OmsConditionType.ITEM_COST:
      return (
        <IntegerComparisonDetails
          field={getConditionTypeLabel(OmsConditionType.ITEM_COST)}
          matchType={step.matchType}
          matchValue={step.matchValue as number | undefined}
          prefix="$"
        />
      );
    case OmsConditionType.LINE_ITEM_CUSTOM_ATTRIBUTE:
      return (
        <FieldToListMatchDetails
          field={`${getConditionTypeLabel(OmsConditionType.LINE_ITEM_CUSTOM_ATTRIBUTE)}${step.matchKey ? ` "${step.matchKey}"` : ""}`}
          fieldLabelIsPlural={false}
          matchType={step.matchType}
          matchValues={step.matchValues}
        />
      );
    case undefined:
      return <Text textColor="disabled">{t("Choose a condition")}</Text>;
    default:
      return <Text textColor="disabled">{t("Unknown condition")}</Text>;
  }
}

export const WorkOrderConditionNode = memo(function WorkOrderConditionNode({
  id,
  data,
  selected,
}: {
  id: string;
  data: WorkOrderConditionNodeData;
  selected?: boolean;
}) {
  const { t } = useTranslation();
  return (
    <BaseNode
      icon={<RedoFeaturedIcon color="warning" Icon={BranchIcon} size="xs" />}
      onDelete={() => data.deleteElements(id)}
      selected={selected}
      step={data.step}
      title={t("Condition")}
    >
      <>
        <Handle position={Position.Top} type="target" />
        <Flex
          className={NodeCSS.fullInput}
          fontSize="xs"
          gap="none"
          wrap="wrap"
        >
          If {getDetails(data.step)}
        </Flex>
        <Handle id="a" position={Position.Bottom} type="source" />
        <Handle id="b" position={Position.Bottom} type="source" />
      </>
    </BaseNode>
  );
});
